import axios from 'axios';
import React, { useState, useEffect } from 'react';
import { 
  Settings as SettingsIcon, Sparkles, Palette, Globe, Bell, Shield,
  Save, User, Users, Layout, MessageCircle, DollarSign, Database,
  ChevronDown, ChevronUp, Monitor, BarChart3
} from 'lucide-react';
import { Header } from '../components/Header';
import { PushToggle } from '../components/PushToggle';
import { NoahVoiceSettings } from '../components/NoahVoiceSettings';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { useApp } from '../contexts/AppContext';
import { useTheme } from '../contexts/ThemeContext';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const SettingsSection = ({ title, icon: Icon, description, children, defaultExpanded = false }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  return (
    <Card className="glass-card overflow-hidden">
      <div
        className="p-4 cursor-pointer hover:bg-muted/30 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Icon className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold">{title}</h3>
              <p className="text-sm text-muted-foreground">{description}</p>
            </div>
          </div>
          {expanded ? <ChevronUp className="w-5 h-5 text-muted-foreground" /> : <ChevronDown className="w-5 h-5 text-muted-foreground" />}
        </div>
      </div>
      {expanded && (
        <div className="border-t border-border p-6 animate-expand-in">
          {children}
        </div>
      )}
    </Card>
  );
};

const API = process.env.REACT_APP_BACKEND_URL;

const Settings = () => {
  const { socialAccounts, saveSocialAccounts, saveAISettings } = useApp();
  const { theme, setTheme, themes } = useTheme();

  const [aiFormData, setAiFormData] = useState({
    greeting_message: '¡Hola! Soy Noah, tu asesor personalizado. ¿En qué puedo ayudarte hoy?',
    system_prompt: 'Eres Noah, un asesor de ventas profesional y amigable. Tu objetivo es ayudar a los clientes, responder sus preguntas y guiarlos hacia una compra. NUNCA menciones que eres una IA o un bot.',
    auto_respond: true,
    auto_classify: true,
    response_language: 'auto',
    response_tone: 'professional',
    max_response_length: 'medium'
  });

  const [displaySettings, setDisplaySettings] = useState({
    cardsLayout: 'grid',
    defaultExpandCards: false,
    showClientValue: true,
    showCampaignBadges: true,
    dashboardWidgets: ['kpis', 'campaigns', 'chats', 'noah'],
    dateFormat: 'DD/MM/YYYY',
    currency: 'USD'
  });

  const [notifSettings, setNotifSettings] = useState({
    newMessage: true,
    newCustomer: true,
    salesComplete: true,
    aiResponse: false,
    soundEnabled: true
  });

  const [accounts, setAccounts] = useState(socialAccounts);
  const [newAccount, setNewAccount] = useState({ platform: 'whatsapp', account_name: '' });

  useEffect(() => {
    setAccounts(socialAccounts);
  }, [socialAccounts]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await fetch(`${API}/api/ai-settings`, { headers: { Authorization: axios.defaults.headers.common["Authorization"] } });
        if (res.ok) {
          const data = await res.json();
          setAiFormData(prev => ({ ...prev, ...data }));
        }
      } catch (err) { console.warn('Settings load error:', err?.message); }
    };
    fetchSettings();
  }, []);

  const handleSaveAI = async () => {
    try {
      await saveAISettings(aiFormData);
      toast.success('Configuración de Noah IA guardada');
    } catch (e) {
      toast.error('Error al guardar');
    }
  };

  const handleAddAccount = () => {
    if (!newAccount.account_name.trim()) return;
    const account = {
      id: Date.now().toString(),
      ...newAccount,
      connected: true
    };
    const updated = [...accounts, account];
    setAccounts(updated);
    saveSocialAccounts(updated);
    setNewAccount({ platform: 'whatsapp', account_name: '' });
    toast.success('Cuenta agregada');
  };

  const handleRemoveAccount = (id) => {
    const updated = accounts.filter(a => a.id !== id);
    setAccounts(updated);
    saveSocialAccounts(updated);
    toast.success('Cuenta eliminada');
  };

  return (
    <div data-testid="settings-page" className="min-h-screen">
      <Header title="Configuración" subtitle="Personaliza DEAL APP a tu medida" />

      <div className="p-6 space-y-4 max-w-4xl">
        {/* Noah AI Settings */}
        <SettingsSection
          title="Configuración de Noah IA"
          icon={Sparkles}
          description="Personaliza cómo Noah responde y gestiona las conversaciones"
          defaultExpanded={true}
        >
          <div className="space-y-6">
            <div>
              <Label>Mensaje de bienvenida</Label>
              <Textarea
                data-testid="greeting-input"
                value={aiFormData.greeting_message}
                onChange={(e) => setAiFormData({ ...aiFormData, greeting_message: e.target.value })}
                placeholder="Mensaje de saludo inicial..."
                rows={2}
              />
            </div>
            <div>
              <Label>Prompt del sistema</Label>
              <p className="text-xs text-muted-foreground mb-1">Define la personalidad y comportamiento de Noah al responder mensajes</p>
              <Textarea
                data-testid="system-prompt-input"
                value={aiFormData.system_prompt}
                onChange={(e) => setAiFormData({ ...aiFormData, system_prompt: e.target.value })}
                rows={4}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Idioma de respuesta</Label>
                <Select value={aiFormData.response_language} onValueChange={v => setAiFormData({ ...aiFormData, response_language: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Automático (detectar)</SelectItem>
                    <SelectItem value="es">Español</SelectItem>
                    <SelectItem value="en">Inglés</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tono de respuesta</Label>
                <Select value={aiFormData.response_tone} onValueChange={v => setAiFormData({ ...aiFormData, response_tone: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="professional">Profesional</SelectItem>
                    <SelectItem value="casual">Casual</SelectItem>
                    <SelectItem value="friendly">Amigable</SelectItem>
                    <SelectItem value="formal">Formal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Extensión de respuestas</Label>
                <Select value={aiFormData.max_response_length} onValueChange={v => setAiFormData({ ...aiFormData, max_response_length: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="short">Cortas</SelectItem>
                    <SelectItem value="medium">Medianas</SelectItem>
                    <SelectItem value="long">Largas</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Respuesta automática</Label>
                  <p className="text-sm text-muted-foreground">Noah responderá automáticamente a los mensajes de clientes</p>
                </div>
                <Switch
                  data-testid="auto-respond-switch"
                  checked={aiFormData.auto_respond}
                  onCheckedChange={(checked) => setAiFormData({ ...aiFormData, auto_respond: checked })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Clasificación automática</Label>
                  <p className="text-sm text-muted-foreground">Noah clasificará el estado del cliente según la conversación</p>
                </div>
                <Switch
                  checked={aiFormData.auto_classify}
                  onCheckedChange={(checked) => setAiFormData({ ...aiFormData, auto_classify: checked })}
                />
              </div>
            </div>

            <Button onClick={handleSaveAI} className="bg-primary hover:opacity-90 gap-2">
              <Save className="w-4 h-4" /> Guardar configuración IA
            </Button>
          </div>
        </SettingsSection>

        {/* Push notifications */}
        <SettingsSection
          title="Notificaciones Push"
          icon={Bell}
          description="Recibe avisos en el celular aunque la app esté cerrada"
        >
          <PushToggle />
        </SettingsSection>

        {/* Voz de Noah */}
        <SettingsSection
          title="Voz de Noah"
          icon={MessageCircle}
          description="Elige con qué voz habla Noah en el resumen diario y los audios"
        >
          <NoahVoiceSettings />
        </SettingsSection>

        {/* Display & Layout */}
        <SettingsSection
          title="Presentación y Diseño"
          icon={Layout}
          description="Controla cómo se muestra la información en la app"
        >
          <div className="space-y-6">
            <div>
              <Label>Tema visual</Label>
              <div className="grid grid-cols-4 gap-3 mt-2">
                {Object.entries(themes).map(([key, config]) => (
                  <button
                    key={key}
                    data-testid={`theme-${key}`}
                    className={cn(
                      "p-4 rounded-xl border-2 transition-all text-center",
                      theme === key ? "border-primary scale-105" : "border-border hover:border-primary/50"
                    )}
                    onClick={() => setTheme(key)}
                  >
                    <div className="w-8 h-8 mx-auto mb-2 rounded-lg" style={{
                      backgroundColor: key === 'dark' ? '#001f1e' : key === 'light' ? '#ffffff' : key === 'colorful' ? '#002d2c' : '#0a0a0a'
                    }} />
                    <p className="text-xs font-medium capitalize">{key === 'midnight' ? 'Medianoche' : key === 'colorful' ? 'Colorido' : key === 'light' ? 'Claro' : 'Oscuro'}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Diseño de tarjetas</Label>
                <Select value={displaySettings.cardsLayout} onValueChange={v => setDisplaySettings({...displaySettings, cardsLayout: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="grid">Cuadrícula</SelectItem>
                    <SelectItem value="list">Lista</SelectItem>
                    <SelectItem value="compact">Compacto</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Formato de fecha</Label>
                <Select value={displaySettings.dateFormat} onValueChange={v => setDisplaySettings({...displaySettings, dateFormat: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DD/MM/YYYY">DD/MM/AAAA</SelectItem>
                    <SelectItem value="MM/DD/YYYY">MM/DD/AAAA</SelectItem>
                    <SelectItem value="YYYY-MM-DD">AAAA-MM-DD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Moneda</Label>
                <Select value={displaySettings.currency} onValueChange={v => setDisplaySettings({...displaySettings, currency: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD ($)</SelectItem>
                    <SelectItem value="EUR">EUR (€)</SelectItem>
                    <SelectItem value="MXN">MXN ($)</SelectItem>
                    <SelectItem value="COP">COP ($)</SelectItem>
                    <SelectItem value="ARS">ARS ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Expandir tarjetas por defecto</Label>
                  <p className="text-sm text-muted-foreground">Las tarjetas informativas se abren expandidas</p>
                </div>
                <Switch
                  checked={displaySettings.defaultExpandCards}
                  onCheckedChange={checked => setDisplaySettings({...displaySettings, defaultExpandCards: checked})}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Mostrar valor de clientes</Label>
                  <p className="text-sm text-muted-foreground">Muestra el valor monetario en las listas</p>
                </div>
                <Switch
                  checked={displaySettings.showClientValue}
                  onCheckedChange={checked => setDisplaySettings({...displaySettings, showClientValue: checked})}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Mostrar badges de campaña</Label>
                  <p className="text-sm text-muted-foreground">Etiquetas de campaña visibles en clientes</p>
                </div>
                <Switch
                  checked={displaySettings.showCampaignBadges}
                  onCheckedChange={checked => setDisplaySettings({...displaySettings, showCampaignBadges: checked})}
                />
              </div>
            </div>

            <Button onClick={() => toast.success('Configuración de diseño guardada')} className="gap-2">
              <Save className="w-4 h-4" /> Guardar diseño
            </Button>
          </div>
        </SettingsSection>

        {/* Social Accounts - Enhanced */}
        <SettingsSection
          title="Redes Sociales"
          icon={Globe}
          description="Conecta y gestiona tus cuentas de redes sociales"
        >
          <div className="space-y-4">
            {[
              { id: 'whatsapp', name: 'WhatsApp Business', color: 'bg-green-500', desc: 'Mensajería directa con clientes', icon: 'WA' },
              { id: 'messenger', name: 'Facebook Messenger', color: 'bg-brand-green', desc: 'Chat desde tu página de Facebook', icon: 'FB' },
              { id: 'instagram', name: 'Instagram DM', color: 'bg-primary', desc: 'Mensajes directos de Instagram', icon: 'IG' },
              { id: 'tiktok', name: 'TikTok Business', color: 'bg-gray-900', desc: 'Mensajes de TikTok', icon: 'TK' },
              { id: 'telegram', name: 'Telegram Bot', color: 'bg-brand-green', desc: 'Bot automatizado en Telegram', icon: 'TG' }
            ].map(platform => {
              const connected = accounts.find(a => a.platform === platform.id);
              return (
                <div key={platform.id} className="flex items-center justify-between p-4 rounded-xl border border-border hover:bg-muted/20 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center text-white text-xs font-bold", platform.color)}>
                      {platform.icon}
                    </div>
                    <div>
                      <p className="font-medium text-sm">{platform.name}</p>
                      <p className="text-xs text-muted-foreground">{platform.desc}</p>
                      {connected && (
                        <p className="text-xs text-emerald-700 dark:text-emerald-400 mt-0.5">Conectada: {connected.account_name}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {connected ? (
                      <>
                        <Badge className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30">Activa</Badge>
                        <Button variant="destructive" size="sm" className="text-xs" 
                          onClick={() => handleRemoveAccount(connected.id)}>
                          Desconectar
                        </Button>
                      </>
                    ) : (
                      <Button variant="outline" size="sm" className="gap-1" onClick={() => {
                        const name = window.prompt(`Nombre de tu cuenta de ${platform.name}:`);
                        if (name) {
                          const account = { id: Date.now().toString(), platform: platform.id, account_name: name, connected: true };
                          const updated = [...accounts, account];
                          setAccounts(updated);
                          saveSocialAccounts(updated);
                          toast.success(`${platform.name} conectada (simulada)`);
                        }
                      }}>
                        Conectar
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
            <p className="text-xs text-muted-foreground text-center pt-2">
              Las conexiones son simuladas. La integración real con APIs estará disponible próximamente.
            </p>
          </div>
        </SettingsSection>

        {/* Notifications */}
        <SettingsSection
          title="Notificaciones"
          icon={Bell}
          description="Controla qué notificaciones recibes"
        >
          <div className="space-y-3">
            {[
              { key: 'newMessage', label: 'Nuevo mensaje', desc: 'Cuando un cliente envía un mensaje' },
              { key: 'newCustomer', label: 'Nuevo cliente', desc: 'Cuando se agrega un nuevo cliente' },
              { key: 'salesComplete', label: 'Venta completada', desc: 'Cuando se cierra una venta' },
              { key: 'aiResponse', label: 'Respuesta IA', desc: 'Cuando Noah responde automáticamente' },
              { key: 'soundEnabled', label: 'Sonidos', desc: 'Habilitar sonidos de notificación' }
            ].map(notif => (
              <div key={notif.key} className="flex items-center justify-between">
                <div>
                  <Label>{notif.label}</Label>
                  <p className="text-sm text-muted-foreground">{notif.desc}</p>
                </div>
                <Switch
                  checked={notifSettings[notif.key]}
                  onCheckedChange={checked => setNotifSettings({...notifSettings, [notif.key]: checked})}
                />
              </div>
            ))}
            <Button onClick={() => toast.success('Notificaciones guardadas')} className="gap-2 mt-4">
              <Save className="w-4 h-4" /> Guardar notificaciones
            </Button>
          </div>
        </SettingsSection>

        {/* Data & Export */}
        <SettingsSection
          title="Datos y Exportación"
          icon={Database}
          description="Exporta datos y gestiona la información"
        >
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => toast.info('Próximamente: Exportar clientes')}>
                <Users className="w-5 h-5" />
                <span className="text-sm">Exportar Clientes</span>
                <span className="text-xs text-muted-foreground">Excel / CSV</span>
              </Button>
              <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => toast.info('Próximamente: Exportar analytics')}>
                <BarChart3 className="w-5 h-5" />
                <span className="text-sm">Exportar Analytics</span>
                <span className="text-xs text-muted-foreground">PDF / Excel</span>
              </Button>
              <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => toast.info('Próximamente: Exportar chats')}>
                <MessageCircle className="w-5 h-5" />
                <span className="text-sm">Exportar Chats</span>
                <span className="text-xs text-muted-foreground">JSON / CSV</span>
              </Button>
              <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => toast.info('Próximamente: Backup completo')}>
                <Database className="w-5 h-5" />
                <span className="text-sm">Backup Completo</span>
                <span className="text-xs text-muted-foreground">Todos los datos</span>
              </Button>
            </div>
          </div>
        </SettingsSection>
      </div>
    </div>
  );
};

export default Settings;
